<template>
  <div class="item-list">
    <button
      v-for="row in rows"
      :key="row.item.id"
      type="button"
      class="item-list__row"
      :class="{ 'item-list__row--selected': row.item.id === selectedId }"
      :disabled="row.locked"
      @click="$emit('select', row.item)"
    >
      <span class="item-list__thumbnail">
        <b-img-lazy
          v-if="row.thumbnail"
          :src="row.thumbnail"
          alt="Thumbnail"
          @error.native="markBroken($event.target.src)"
        />
        <LucideIcon
          v-else
          name="film"
          :size="16"
        />
      </span>

      <span class="item-list__name">{{ row.item.name }}</span>

      <span
        v-if="row.company"
        class="item-list__meta"
      >
        {{ row.company }}
      </span>

      <span
        v-if="row.tags"
        class="item-list__meta"
      >
        {{ row.tags }}
      </span>

      <span class="item-list__meta item-list__updated">{{ row.statusLabel || row.updated }}</span>
    </button>
  </div>
</template>

<script>
import { timeAgo } from '../../utils/time-ago'
import LucideIcon from './atoms/LucideIcon.vue'
import { ASSET_COPY_FAILED, DUPLICATING_STATUSES } from '../../constants/video-projects'

const THUMBNAIL_SCALE = 112

// Only `/share` resizes on the fly (ImageCacheService); a plain `/storage` path has to be left alone.
function thumbnailUrl (path) {
  return path.includes('/share?') ? `${path}&lightweight=1&scale=${THUMBNAIL_SCALE}&format=webp` : path
}

export default {
  name: 'ViewList',

  components: {
    LucideIcon,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    duplicatingId: {
      type: Number,
      default: null,
    },
    // Only a list that picks a row instead of opening it passes this.
    selectedId: {
      type: [Number, String],
      default: null,
    },
  },

  data () {
    return {
      brokenThumbnails: {},
    }
  },

  computed: {
    rows () {
      return this.items.map(item => {
        const thumbnail = item.thumbnails?.length ? thumbnailUrl(item.thumbnails[0].path) : ''

        return {
          item,
          locked: this.isLocked(item),
          statusLabel: this.statusLabel(item),
          company: item.company ? item.company.name : '',
          tags: (item.tags || []).map(tag => tag.name.en).join(', '),
          updated: timeAgo(item.updated_at),
          thumbnail: this.brokenThumbnails[thumbnail] ? '' : thumbnail,
        }
      })
    },
  },

  methods: {
    // Same rule as the gallery: a copy is not safe to open until its assets are its own.
    isLocked (item) {
      return item.id === this.duplicatingId
        || DUPLICATING_STATUSES.includes(item.asset_copy_status)
        || item.asset_copy_status === ASSET_COPY_FAILED
    },
    statusLabel (item) {
      if (item.asset_copy_status === ASSET_COPY_FAILED) {
        return 'Duplication failed'
      }

      return this.isLocked(item) ? 'Duplicating...' : ''
    },
    // A thumbnail row can outlive its S3 file; `/share` then answers with an HTML error icon.
    markBroken (url) {
      this.$set(this.brokenThumbnails, url, true)
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/../sass/frontend/variables";

.item-list {
  overflow: hidden;
  border: 1px solid $greyscale100;
  border-radius: 6px;
  background-color: #fff;

  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    border: 0;
    border-top: 1px solid $greyscale100;
    background-color: transparent;
    font-size: 14px;
    text-align: left;

    &:first-child {
      border-top: 0;
    }

    &:hover {
      background-color: $greyscale50;
    }

    &--selected,
    &--selected:hover {
      background-color: $greyscale100;
    }
  }

  &__thumbnail {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 36px;
    overflow: hidden;
    border-radius: 4px;
    background: linear-gradient(to right bottom, rgb(245, 245, 245), rgb(229, 229, 229));
    color: $greyscale200;

    img {
      width: 100%;
      height: 100%;
      // `cover` crops these product shots to a meaningless slice at 56x36.
      object-fit: contain;
    }
  }

  &__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    color: $greyscale400;
    font-size: 14px;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__meta {
    flex-shrink: 0;
    color: $greyscale300;
    font-size: 12px;
  }

  &__updated {
    width: 96px;
    text-align: right;
  }
}
</style>
